import React, { useEffect, useState } from 'react';
import { Button, Typography, message, Divider, Card, Tag, Alert } from 'antd';
import { LinkOutlined, CheckCircleFilled, SaveOutlined } from '@ant-design/icons';
import CommonTable from '../../../global/table';
import CommonCheckbox from '../../../global/checkbox';
import CommonDialog from '../../../global/dialog';
import CommonLoader from '../../../global/loader';
import { useGenericMutation } from '../../../../queries/use-generic-mutation';
import { cn } from '../../../../utils/cn';
import Spinner from '../../../global/spinner';
import { interceptor } from '../../../../utils/interceptor';
import LocalStorageService from '../../../../utils/localStorageServices';

const { Title, Text } = Typography;

const localStorageService = LocalStorageService.getService();
const axios = interceptor();

/**
 * TripleseatOAuth2
 *
 * Props:
 *  - isOAuth2Connected {boolean}  — whether the OAuth 2.0 token already exists
 *  - btnClassName      {string}   — matches the parent's button theme class
 *  - integrationKey    {string}   — the DESTINATION_TRIPLESEAT constant value
 *  - config                   {object}   — the destination config object with extraInfo
 *  - isUserConnectedToOAuth2 {boolean} — user has migrated to OAuth 2.0 (permanent; hides legacy UI)
 *  - onOAuthConnectionChange {function} — called when OAuth connection state changes
 */
const TripleseatOAuth2 = ({
    isOAuth2Connected,
    isUserConnectedToOAuth2,
    btnClassName,
    integrationKey,
    config,
    onOAuthConnectionChange,
}) => {
    const [locations, setLocations] = useState([]);
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [isConnecting, setIsConnecting] = useState(false);
    const [showLoaderDialog, setShowLoaderDialog] = useState(false);

    const currentUserEmail = localStorageService.getCurrentUser()?.user?.email;

    const isConnected = !!isOAuth2Connected;
    const hasMigratedToOAuth2 = isUserConnectedToOAuth2 || config?.extraInfo?.isUserConnectedToOAuth2 || false;

    // ─── Mutations ────────────────────────────────────────────────────────────

    const { mutate: getLocations, isPending: isLocLoading } = useGenericMutation({
        endpoint: 'integration/tripleseat-locations',
        method: 'POST',
        onSuccess: (data) => setLocations(data?.data || []),
        onError: () => message.error('Error fetching Tripleseat locations'),
    });

    const { mutate: saveTripleSeat, isPending: isSaving } = useGenericMutation({
        endpoint: 'integration',
        method: 'POST',
        onSuccess: () => message.success('Tripleseat configuration saved successfully'),
        onError: () => message.error('Failed to save configuration'),
    });

    const { mutate: exchangeCode } = useGenericMutation({
        endpoint: 'integration/tripleseat/callback',
        method: 'POST',
        onSuccess: (response) => {
            message.success('Tripleseat connected successfully!');
            setShowLoaderDialog(false);
            onOAuthConnectionChange?.({
                isConnected: !!response?.data?.isConnected,
                isUserConnectedToOAuth2: !!response?.data?.isUserConnectedToOAuth2,
            });
        },
        onError: () => {
            message.error('Failed to connect Tripleseat');
            setShowLoaderDialog(false);
        },
    });

    const { mutate: disconnectTripleseat } = useGenericMutation({
        endpoint: 'integration/tripleseat/disconnect',
        method: 'DELETE',
        onSuccess: (response) => {
            message.success('Tripleseat disconnected');
            setLocations([]);
            onOAuthConnectionChange?.({
                isConnected: response?.data?.isConnected ?? false,
                isUserConnectedToOAuth2: response?.data?.isUserConnectedToOAuth2 ?? hasMigratedToOAuth2,
            });
        },
        onError: () => message.error('Failed to disconnect'),
    });

    // ─── Effects ──────────────────────────────────────────────────────────────

    // Handle OAuth 2.0 redirect callback (?code=…&state=…)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const state = params.get('state');

        if (code && state) {
            // Show the dialog (in case page reloads)
            setShowLoaderDialog(true);
            exchangeCode({ code, userEmail: state });
            window.history.replaceState({}, '', window.location.pathname + '?tab=2');
        }
    }, []);

    // Auto-load locations once connected
    useEffect(() => {
        if (isConnected) {
            getLocations({ oauthMode: true });
            // Pre-select locations from config
            if (config?.locations) {
                setSelectedLocations(config.locations);
            }
        }
    }, [isConnected, config]);

    // ─── Handlers ─────────────────────────────────────────────────────────────

    const handleOAuth2Connect = () => {
        setIsConnecting(true); // just for button spinner UX

        if (!process.env.REACT_APP_TRIPLESEAT_CLIENT_ID) {
            message.error('Tripleseat Client ID is not defined');
            setIsConnecting(false);
            return;
        }

        if (!currentUserEmail) {
            message.error('User Email is not defined');
            setIsConnecting(false);
            return;
        }

        // Show loader dialog before redirecting
        setShowLoaderDialog(true);

        window.location.href =
            `https://login.tripleseat.com/oauth2/authorize`
            + `?client_id=${process.env.REACT_APP_TRIPLESEAT_CLIENT_ID}`
            + `&redirect_uri=https://app.leadsimport.com/destinations?tab=2`
            + `&response_type=code`
            + `&scope=read%20write`
            + `&state=${currentUserEmail}`;
    };

    const handleDisconnect = () => {
        disconnectTripleseat({ userEmail: currentUserEmail });
    };

    const handleLocationSelect = (id, checked) => {
        if (checked) {
            setSelectedLocations((prev) => [...prev, id.toString()]);
        } else {
            setSelectedLocations((prev) => prev.filter((locId) => locId !== id.toString()));
        }
    };

    const handleSave = () => {
        saveTripleSeat({
            integrationCompany: integrationKey,
            locations: selectedLocations,
        });
    };

    // ─── Table columns ────────────────────────────────────────────────────────

    const columns = [
        {
            title: 'Select',
            dataIndex: 'id',
            key: 'id',
            width: 80,
            render: (id) => (
                <CommonCheckbox
                    checked={selectedLocations.includes(id.toString())}
                    onChange={(e) => handleLocationSelect(id, e.target.checked)}
                />
            ),
        },
        {
            title: 'Location Name',
            dataIndex: 'name',
            key: 'name',
            render: (text) => <Text strong>{text}</Text>,
        },
    ];

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <>
            {/* Loader Dialog for OAuth Callback */}
            <CommonDialog
                title="Connecting to Tripleseat"
                description="Please wait while we connect your Tripleseat account..."
                open={showLoaderDialog}
                setOpen={() => {}}
                onOk={() => {}}
                onCancel={() => {}}
                footer={null} // Hide footer
                className="!rounded-xl"
            >
                <CommonLoader
                    text="Connecting to Tripleseat..."
                    minHeight="200px"
                />
            </CommonDialog>

            {/* Divider — only when legacy UI is still available */}
            {!isConnected && !hasMigratedToOAuth2 && (
                <Divider className="my-6">
                    <Text type="secondary" className="text-xs">
                        or connect with OAuth 2.0 (Recommended)
                    </Text>
                </Divider>
            )}

            {/* ── Connect card (unconnected) ── */}
            {!isConnected && (
                <Card className="rounded-2xl border-none shadow-sm transition-all animate-in fade-in duration-500">
                    <div className="flex items-center gap-2 mb-1">
                        <Tag color="green" className="text-xs font-semibold">New</Tag>
                        <Title level={5} className="!mb-0">Connect with OAuth 2.0</Title>
                    </div>
                    <Text type="secondary" className="block mb-4 text-sm">
                        More secure. No manual key entry. Auto-refreshing tokens.
                    </Text>

                    <Alert
                        type="info"
                        showIcon
                        className="mb-4 rounded-xl"
                        message={
                            <span>
                                OAuth 1.0 is being deprecated by Tripleseat on{' '}
                                <strong>July 1, 2026</strong>. Connecting via OAuth 2.0 ensures
                                uninterrupted access.
                            </span>
                        }
                    />

                    <Button
                        size="large"
                        icon={isConnecting ? null : <LinkOutlined />}
                        onClick={handleOAuth2Connect}
                        className={cn(btnClassName, 'w-full')}
                        disabled={isConnecting}
                    >
                        {isConnecting && <Spinner className="!border-white" />}
                        Connect Tripleseat via OAuth 2.0
                    </Button>
                </Card>
            )}

            {/* ── Connected card ── */}
            {isConnected && (
                <Card className="rounded-2xl border border-green-100 shadow-sm transition-all animate-in fade-in duration-500">
                    <div className="flex items-center gap-2 mb-1">
                        <CheckCircleFilled style={{ color: '#22c55e', fontSize: 20 }} />
                        <Title level={5} className="!mb-0">Tripleseat Connected</Title>
                    </div>
                    <Text type="secondary" className="block mb-5 text-sm">
                        OAuth 2.0 · Auto-refreshing tokens
                    </Text>

                    {/* Locations table */}
                    {isLocLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <Spinner className="!border-primary" />
                        </div>
                    ) : locations.length > 0 ? (
                        <div className="mb-5 animate-in slide-in-from-top-2 duration-300">
                            <Text
                                type="secondary"
                                className="block mb-2 text-xs font-semibold uppercase tracking-wider"
                            >
                                Available Locations
                            </Text>
                            <CommonTable columns={columns} data={locations} showActions={false} />
                        </div>
                    ) : null}

                    <Divider className="mb-4 border-gray-100" />

                    <div className="flex items-center justify-between gap-4">
                        <Button
                            size="large"
                            icon={isSaving ? null : <SaveOutlined />}
                            onClick={handleSave}
                            className={cn(btnClassName)}
                        >
                            {isSaving && <Spinner className="!border-white" />}
                            Save Configuration
                        </Button>

                        <Button
                            type="link"
                            danger
                            onClick={handleDisconnect}
                            className="ml-auto"
                        >
                            Disconnect
                        </Button>
                    </div>
                </Card>
            )}
        </>
    );
};

export default TripleseatOAuth2;
